import { useState } from 'react';
import { Box, Flex, Grid, GridItem, Text, Tooltip } from '@chakra-ui/react';
import { Button, ButtonText } from '@morressier/alembic';
import { IBlockedReviewer } from '@publishing-engine/domain/views';
import ReviewerSearch from './InviteReviewerBySearch';
import SubmissionInfoWithAuthors from '../SubmissionInfoWithAuthors';
import { ExistingReviewersProvider } from '../../../../contexts/ExistingReviewersProvider';
import { useSubmissionData } from '../../../../contexts/SubmissionDataProvider';
import AddEmailsModal from './AddEmailsModal';
import ReviewersListSelectionAccountCreation from '../ReviewerSelection/ReviewersListSelectionAccountCreation';
import { useReviewerInviteList } from '../../../../contexts/ReviewerInviteListProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faAt,
  faInfoCircle
} from '@fortawesome/pro-regular-svg-icons';
import { COCSubmissionAssignment } from '../../../../pages/SubmissionDetails/COCSubmissionAssignment';

interface Props {
  closeModal: () => void;
  accountCreationEnabled: boolean;
  blockedReviewers?: IBlockedReviewer[];
}

const AddReviewerWithSearch = ({
  closeModal,
  accountCreationEnabled,
  blockedReviewers = []
}: Props) => {
  const [addEmailsModalOpen, setAddEmailsModalOpen] = useState(false);
  const { reviewerList, addReviewersByEmails } = useReviewerInviteList();
  const { submissionId } = useSubmissionData();

  return (
    <>
      <Grid templateColumns="repeat(12, 1fr)" gap={2}>
        <GridItem colSpan={12}>
          <ButtonText variant="primary" onClick={closeModal}>
            <FontAwesomeIcon icon={faArrowLeft} /> Back
          </ButtonText>
          <Flex flexDirection="row" justifyContent="space-between">
            <Flex alignItems="center">
              <Text textStyle="headingSection">Search & Add Reviewers</Text>
              <Tooltip
                hasArrow
                shouldWrapChildren
                placement="top"
                label={
                  <Text textStyle="textContext">
                    Use keywords and filters to find reviewers by experience,
                    field, or availability. Easily add candidates to your invite
                    list
                  </Text>
                }
                borderRadius={4}
                data-cy="info-tooltip"
              >
                <ButtonText>
                  <FontAwesomeIcon icon={faInfoCircle} />
                </ButtonText>
              </Tooltip>
            </Flex>
            <Button
              leftIcon={<FontAwesomeIcon size="sm" icon={faAt} />}
              onClick={() => setAddEmailsModalOpen(true)}
            >
              Add by Email
            </Button>
          </Flex>
        </GridItem>
        <GridItem colSpan={8}>
          <Box mb={2}>
            <SubmissionInfoWithAuthors />
          </Box>

          <ExistingReviewersProvider
            existingReviewers={[
              ...reviewerList.map(f => ({ email: f.email })) // 👈 merge current form reviewers
            ]}
          >
            <ReviewerSearch />
          </ExistingReviewersProvider>
        </GridItem>
        <GridItem colSpan={4}>
          <Box mb={2}>
            <COCSubmissionAssignment
              submissionId={submissionId}
              readOnly
              defaultExpanded={false}
            />
          </Box>

          <ReviewersListSelectionAccountCreation
            isAccountCreationEnabled={accountCreationEnabled}
          />
        </GridItem>
      </Grid>

      {addEmailsModalOpen && (
        <ExistingReviewersProvider
          existingReviewers={[
            ...reviewerList.map(f => ({ email: f.email })) // 👈 merge current form reviewers
          ]}
        >
          <AddEmailsModal
            onClose={() => setAddEmailsModalOpen(false)}
            onAddEmails={(emails: string[]) => addReviewersByEmails(emails)}
            blockedReviewers={blockedReviewers}
          />
        </ExistingReviewersProvider>
      )}
    </>
  );
};

export default AddReviewerWithSearch;
